import React, { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "../../core/api";
import { useApp } from "../../core/AppContext";
import { fa, formatDate, todayIso } from "../../core/format";
import Modal from "../../components/Modal";
import ResponsiveList from "../../components/ResponsiveList";
import FileViewer from "../../components/FileViewer";
import MultiFileField from "../../components/MultiFileField";
import PrintReport from "../../components/PrintReport";
import { managerLetterPrint } from "../../core/printConfigs";

const KIND = {
  manager: "نامه‌ی مدیر بندر",
  reply: "پاسخ مسئول ایمنی",
  supplementary: "نامه‌ی تکمیلی",
  staff_reply: "پاسخ کارکن",
};

const sizeText = (b) =>
  b >= 1048576
    ? `${fa((b / 1048576).toFixed(1))} مگابایت`
    : `${fa(Math.max(1, Math.round(b / 1024)))} کیلوبایت`;
const fileIcon = (m) => (m === "application/pdf" ? "fa-regular fa-file-pdf" : "fa-regular fa-image");

/* ---------- ارسال نامه / پاسخ (چندفایله) ---------- */
function ComposeModal({ title, url, initial, withTarget, onClose, onDone }) {
  const { toast } = useApp();
  const [form, setForm] = useState({ subject: initial?.subject || "", body: "", targetType: "shift" });
  const [files, setFiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (withTarget) api.get("/employees").then((r) => setUsers(r.data.items.filter((e) => e.userId))).catch(() => {});
  }, [withTarget]);

  const submit = async () => {
    if (!form.subject.trim() || !form.body.trim()) return setErr("موضوع و شرح نامه را وارد کنید");
    if (withTarget && form.targetType === "users" && !sel.length) return setErr("حداقل یک کاربر را انتخاب کنید");
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("subject", form.subject);
      fd.append("body", form.body);
      if (withTarget) {
        fd.append("targetType", form.targetType);
        if (form.targetType === "users") fd.append("userIds", JSON.stringify(sel));
      }
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(url, fd, { timeout: 180000 });
      toast(`نامه با شماره ${data.letterNo} ثبت شد`);
      onDone();
    } catch (e) { setErr(errMsg(e)); setBusy(false); }
  };

  return (
    <Modal title={title} onClose={() => !busy && onClose()}
      footer={<><button className="btn" onClick={submit} disabled={busy}>{busy ? "در حال ارسال..." : "ارسال"}</button>
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>انصراف</button></>}>
      {err && <div className="alert err">{err}</div>}
      <div className="hint" style={{ marginBottom: 10 }}>تاریخ: {formatDate(todayIso())} · شماره نامه به‌صورت خودکار ثبت می‌شود</div>
      <div className="field"><label>موضوع<span className="req">*</span></label>
        <input value={form.subject} maxLength={250} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
      <div className="field"><label>شرح<span className="req">*</span></label>
        <textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
      <MultiFileField label="فایل‌های پیوست (اختیاری)" files={files} onChange={setFiles} />
      {withTarget && (
        <>
          <div className="tabs">
            <button type="button" className={`tab-btn ${form.targetType === "shift" ? "on" : ""}`} onClick={() => setForm({ ...form, targetType: "shift" })}>شیفتِ امروز</button>
            <button type="button" className={`tab-btn ${form.targetType === "users" ? "on" : ""}`} onClick={() => setForm({ ...form, targetType: "users" })}>کاربران خاص</button>
          </div>
          {form.targetType === "users" && (
            <div className="check-list">
              {users.map((u) => (
                <label key={u.userId} className={`check-item ${sel.includes(u.userId) ? "on" : ""}`} style={{ margin: 0 }}>
                  <input type="checkbox" checked={sel.includes(u.userId)}
                    onChange={() => setSel((s) => (s.includes(u.userId) ? s.filter((x) => x !== u.userId) : [...s, u.userId]))} />
                  {u.firstName} {u.lastName}
                </label>
              ))}
            </div>
          )}
          <div className="hint">نامه‌ی اصلی مدیر بندر (همراه فایل‌هایش) به‌عنوان پیوست همراه این نامه می‌شود.</div>
        </>
      )}
    </Modal>
  );
}

/* ---------- مشاهده ---------- */
function DetailModal({ id, onClose, onViewFile }) {
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/manager-letters/${id}`).then((r) => setItem(r.data.item)).catch((e) => setErr(errMsg(e))); }, [id]);
  const box = { whiteSpace: "pre-wrap", lineHeight: 2, background: "var(--card-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px" };

  const FileList = ({ letterId, legacy, files, subject }) => {
    if (!legacy && !files?.length) return null;
    return (
      <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
        {legacy && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", background: "var(--card)" }}>
            <i className="fa-solid fa-paperclip" style={{ color: "var(--primary)" }} />
            <span style={{ flex: 1 }}>پیوست اصلی</span>
            <button className="icon-btn" title="مشاهده" onClick={() => onViewFile(`/manager-letters/${letterId}/file`, subject)}><i className="fa-regular fa-eye" /></button>
          </div>
        )}
        {files.map((f) => (
          <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", background: "var(--card)" }}>
            <i className={fileIcon(f.mime)} style={{ color: "var(--primary)" }} />
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{f.name}</span>
            <small className="hint" style={{ margin: 0 }}>{sizeText(f.size)}</small>
            <button className="icon-btn" title="مشاهده" onClick={() => onViewFile(`/manager-letters/${letterId}/files/${f.id}`, f.name)}><i className="fa-regular fa-eye" /></button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal wide title="مشاهده‌ی نامه" onClose={onClose} footer={<button className="btn btn-ghost" onClick={onClose}>بستن</button>}>
      {err && <div className="alert err">{err}</div>}
      {!item && !err && <div className="empty"><div className="spinner" style={{ margin: "0 auto" }} /></div>}
      {item && (
        <>
          <div className="chips" style={{ marginBottom: 10 }}>
            <span className="chip">{KIND[item.kind]}</span><span className="chip">شماره: {item.letterNo}</span>
            <span className="chip">تاریخ: {formatDate(item.letterDate)}</span><span className="chip">فرستنده: {item.senderName}</span>
          </div>
          <h3 style={{ marginBottom: 8 }}>{item.subject}</h3>
          <div style={box}>{item.body}</div>
          <FileList letterId={item.id} legacy={item.hasLegacyFile} files={item.files} subject={item.subject} />
          {item.parent && (
            <div className="field" style={{ marginTop: 16 }}>
              <label><i className="fa-solid fa-paperclip" /> پیوست: نامه‌ی {item.parent.letterNo} ({formatDate(item.parent.letterDate)})</label>
              <div style={box}><b>{item.parent.subject}</b><div>{item.parent.body}</div></div>
              <FileList letterId={item.parent.id} legacy={item.parent.hasLegacyFile} files={item.parent.files} subject={item.parent.subject} />
            </div>
          )}
          {item.recipients?.length > 0 && (
            <div className="field" style={{ marginTop: 14 }}><label>گیرندگان</label>
              <div className="chips">{item.recipients.map((r, i) => <span key={i} className={`badge ${r.readAt ? "ok" : "wait"}`}>{r.name}{r.readAt ? " ✓" : ""}</span>)}</div></div>
          )}
        </>
      )}
    </Modal>
  );
}

export default function ManagerLetters() {
const { user, toast, confirm } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compose, setCompose] = useState(null); // {mode,row}
  const [detail, setDetail] = useState(null);
  const [viewer, setViewer] = useState(null);
const [printRow, setPrintRow] = useState(null);


  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/manager-letters")).data.items); } catch (e) { toast(errMsg(e), "err"); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const viewFile = (path, name) => setViewer({ path, name });
  const close = () => setCompose(null);
  const done = () => { setCompose(null); load(); };
const remove = async (r) => {
  if (
    !(await confirm(
      "این نامه همراه با پاسخ‌ها، نامه‌های تکمیلی و فایل‌هایش برای همیشه حذف شود؟ این کار قابل بازگشت نیست.",
    ))
  )
    return;
  try {
    await api.delete(`/manager-letters/${r.id}`);
    toast("نامه حذف شد");
    load();
  } catch (e) {
    toast(errMsg(e), "err");
  }
};
  const subtitle =
    user.role === "manager"
      ? "ارسال نامه به مسئول ایمنی و آتش‌نشانی و مشاهده‌ی پاسخ‌ها"
      : user.role === "admin"
        ? "پاسخ یا ابلاغ نامه‌های مدیر بندر به کاربران و شیفت"
        : "نامه‌های ابلاغ‌شده به شما؛ می‌توانید برای مسئول ایمنی و مدیر بندر پاسخ بفرستید";

  return (
    <>
      <div className="page-head">
        <div><h2>نامه‌های مدیر بندر</h2><p>{subtitle}</p></div>
        {user.role === "manager" && <button className="btn" onClick={() => setCompose({ mode: "new" })}><i className="fa-solid fa-plus" /> نامه‌ی جدید</button>}
      </div>
      <ResponsiveList loading={loading} rows={rows} empty="نامه‌ای وجود ندارد"
        columns={[
          { key: "letterNo", label: "شماره", render: (r) => <b>{r.letterNo}{r.unread ? <span className="badge bad" style={{ marginInlineStart: 6 }}>جدید</span> : null}</b> },
          { key: "letterDate", label: "تاریخ", render: (r) => formatDate(r.letterDate) },
          { key: "kind", label: "نوع", render: (r) => KIND[r.kind] },
          { key: "subject", label: "موضوع", render: (r) => <>{r.subject}{r.hasFile ? <i className="fa-solid fa-paperclip" style={{ marginInlineStart: 8, color: "var(--muted)" }} /> : null}</> },
          { key: "senderName", label: "فرستنده" },
        ]}
        mobileTitle={(r) => r.subject}
        mobileBadge={(r) => <span className="mc-date">{r.letterNo}</span>}
        mobileMeta={(r) => [
          { icon: "fa-regular fa-calendar", label: "تاریخ", value: formatDate(r.letterDate) },
          { icon: "fa-regular fa-envelope", label: "نوع", value: KIND[r.kind] },
          { icon: "fa-solid fa-user", label: "فرستنده", value: r.senderName },
        ]}
        onRowClick={(r) => setDetail(r.id)}
        actions={(r) => (
          <>
          <button className="icon-btn" title="چاپ گزارش" onClick={(e) => { e.stopPropagation(); setPrintRow(r); }}><i className="fa-solid fa-print" /></button>

            <button className="icon-btn" title="مشاهده" onClick={(e) => { e.stopPropagation(); setDetail(r.id); }}><i className="fa-regular fa-eye" /></button>
          {user.role === "admin" && r.kind === "manager" && (
  <>
    <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); setCompose({ mode: "reply", row: r }); }}><i className="fa-solid fa-reply" /> پاسخ</button>
    <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); setCompose({ mode: "forward", row: r }); }}><i className="fa-solid fa-share" /> ابلاغ به کاربران/شیفت</button>
    <button className="icon-btn danger" title="حذف نامه" onClick={(e) => { e.stopPropagation(); remove(r); }}><i className="fa-solid fa-trash" /></button>
  </>
)}
            {user.role === "staff" && !r.mine && ["supplementary", "manager"].includes(r.kind) && (
              <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); setCompose({ mode: "staffReply", row: r }); }}><i className="fa-solid fa-reply" /> پاسخ</button>
            )}
          </>
        )} />

      {compose?.mode === "new" && <ComposeModal title="نامه به مسئول ایمنی و آتش‌نشانی" url="/manager-letters" onClose={close} onDone={done} />}
      {compose?.mode === "reply" && <ComposeModal title="پاسخ به مدیر بندر" url={`/manager-letters/${compose.row.id}/reply`} initial={{ subject: `پاسخ: ${compose.row.subject}` }} onClose={close} onDone={done} />}
      {compose?.mode === "staffReply" && <ComposeModal title="پاسخ برای مسئول ایمنی و مدیر بندر" url={`/manager-letters/${compose.row.id}/staff-reply`} initial={{ subject: `پاسخ: ${compose.row.subject}` }} onClose={close} onDone={done} />}
      {compose?.mode === "forward" && <ComposeModal title="نامه‌ی تکمیلی (ابلاغ)" url={`/manager-letters/${compose.row.id}/forward`} withTarget initial={{ subject: compose.row.subject }} onClose={close} onDone={done} />}
      {detail && <DetailModal id={detail} onClose={() => { setDetail(null); load(); }} onViewFile={viewFile} />}
      {viewer && <FileViewer {...viewer} onClose={() => setViewer(null)} />}
        {printRow && <PrintReport {...managerLetterPrint(printRow)} onClose={() => setPrintRow(null)} />}
    </>
  );
}